import { useState } from 'react';

type Format = 'survival' | 'workbook' | 'bundle';

interface FormatOption {
  id: Format;
  label: string;
  price: string;
  originalPrice?: string;
  badge?: string;
  description: string;
  amazonLink: string;
}

const formats: FormatOption[] = [
  {
    id: 'survival',
    label: 'Survival Guide',
    price: '$14.99',
    description: 'The raw, honest survival guide built from pain, pressure, setbacks, and breakthroughs.',
    amazonLink: 'https://www.amazon.com/FORGOTTEN-FELON-BLUEPRINT-SURVIVAL-GUIDE/dp/B0GQQ2GD8L/ref=tmm_pap_swatch_0?_encoding=UTF8&dib_tag=se&dib=eyJ2IjoiMSJ9.KNTlRGI7lq2evNAeO_w_kA.21AGulJ3SLwEl8_DjggeG24eNdOvOh8rAm-e219fBRc&qid=1773405432&sr=8-1',
  },
  {
    id: 'workbook',
    label: 'Workbook',
    price: '$16.99',
    description: 'The companion workbook to put the blueprint into action — exercises, plans, and accountability.',
    amazonLink: 'https://www.amazon.com/dp/B0GSR1TWHT/ref=sr_1_1?crid=172GEC66V3ON5&dib=eyJ2IjoiMSJ9.uJMnuRUKpex2GIoWSXqxFEUpYYuqNpF7T7ngOMpcWsnGjHj071QN20LucGBJIEps.5_NN09L6-Q6oJPsJrKgLQPAgFcaze_VDitiGRMViiRo&dib_tag=se&keywords=the+forgotten+felon+workbook&qid=1773716016&sprefix=%2Caps%2C112&sr=8-1',
  },
  {
    id: 'bundle',
    label: 'Bundle Deal',
    price: '$24.99',
    originalPrice: '$31.98',
    badge: 'BEST VALUE',
    description: 'Get both the Survival Guide AND the Workbook together. Save $6.99 and get the full system.',
    amazonLink: 'https://www.amazon.com/FORGOTTEN-FELON-BLUEPRINT-SURVIVAL-GUIDE/dp/B0GQQ2GD8L/ref=tmm_pap_swatch_0?_encoding=UTF8&dib_tag=se&dib=eyJ2IjoiMSJ9.KNTlRGI7lq2evNAeO_w_kA.21AGulJ3SLwEl8_DjggeG24eNdOvOh8rAm-e219fBRc&qid=1773405432&sr=8-1',
  },
];

export default function PurchaseSection() {
  const [selectedFormat, setSelectedFormat] = useState<Format>('bundle');

  const current = formats.find(f => f.id === selectedFormat)!;

  return (
    <section id="purchase" className="py-0 bg-stone-900">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px] lg:min-h-[900px]">
        {/* Left Half - Book Cover(s) */}
        <div className="relative overflow-hidden bg-amber-950 flex items-center justify-center p-6 sm:p-10 min-h-[320px] sm:min-h-[420px] lg:min-h-[900px]">
          {selectedFormat === 'bundle' ? (
            <div className="relative flex items-end justify-center gap-3 sm:gap-6 w-full max-w-xs sm:max-w-lg">
              {/* Survival Guide — front/left, bigger */}
              <div className="relative w-40 sm:w-56 md:w-80 shadow-2xl shadow-amber-900/50 -rotate-2 z-10">
                <img
                  src="https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/95e5927307d061922e242261363b9b2d.jpeg"
                  alt="The Forgotten Felon Blueprint Survival Guide by John Reed"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Workbook — behind/right, smaller */}
              <div className="relative w-28 sm:w-44 md:w-60 shadow-2xl shadow-amber-900/40 rotate-3 translate-y-4 sm:translate-y-6">
                <img
                  src="https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/2e6f5304253a2f72a1e4ba066b3987d8.jpeg"
                  alt="The Forgotten Felon Workbook by John Reed"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -top-3 -right-1 sm:-top-4 sm:-right-2 bg-amber-500 text-stone-900 text-[9px] sm:text-xs font-black uppercase tracking-widest px-2 py-1 sm:px-3 sm:py-1.5 rotate-3 shadow-lg z-20">
                Bundle Deal
              </div>
            </div>
          ) : selectedFormat === 'workbook' ? (
            <div className="relative w-48 sm:w-64 md:w-96 shadow-2xl shadow-amber-900/30">
              <img
                src="https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/2e6f5304253a2f72a1e4ba066b3987d8.jpeg"
                alt="The Forgotten Felon Workbook by John Reed"
                className="w-full h-auto object-cover"
              />
            </div>
          ) : (
            <div className="relative w-52 sm:w-72 md:w-[420px] shadow-2xl shadow-amber-900/30">
              <img
                src="https://static.readdy.ai/image/b0bf0689308dc3dbfe962826cee4efbe/95e5927307d061922e242261363b9b2d.jpeg"
                alt="The Forgotten Felon Blueprint Survival Guide by John Reed"
                className="w-full h-auto object-cover"
              />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-950/60 via-transparent to-stone-900/40 pointer-events-none"></div>
          <div className="absolute top-4 left-4 sm:top-8 sm:left-8 px-3 py-1.5 sm:px-4 sm:py-2 border border-amber-500 text-[10px] sm:text-xs uppercase tracking-widest text-amber-400 font-bold bg-amber-950/70 backdrop-blur-sm">
            NOW AVAILABLE
          </div>
        </div>

        {/* Right Half - Purchase Content */}
        <div className="bg-stone-900 border-t lg:border-t-0 lg:border-l border-amber-900/40 flex items-center justify-center px-5 py-10 sm:px-8 sm:py-14 lg:px-16">
          <div className="max-w-md w-full">
            <p className="text-xs uppercase tracking-widest text-amber-400 mb-3 md:mb-4">Get Your Copy</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-amber-50 mb-2 leading-tight uppercase">
              The Forgotten
            </h2>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-amber-50 mb-2 leading-tight uppercase">
              Felon
            </h2>
            <h3 className="text-lg sm:text-xl font-black text-amber-400 mb-2 uppercase tracking-widest">
              Blueprint
            </h3>
            <div className="w-12 h-1 bg-amber-600 mb-6 md:mb-8"></div>

            {/* Format Selector */}
            <div className="flex items-stretch gap-1.5 sm:gap-2 mb-6 md:mb-8">
              {formats.map((format) => (
                <button
                  key={format.id}
                  onClick={() => setSelectedFormat(format.id)}
                  className={`relative flex-1 px-1.5 sm:px-3 py-2.5 sm:py-3 text-[10px] sm:text-xs font-bold uppercase tracking-wide transition-all duration-300 cursor-pointer text-center ${
                    selectedFormat === format.id
                      ? 'bg-amber-600 text-amber-50'
                      : 'border border-amber-800/60 text-amber-300/60 hover:border-amber-500 hover:text-amber-400'
                  }`}
                >
                  {format.badge && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-400 text-stone-900 text-[8px] sm:text-[9px] font-black px-1.5 sm:px-2 py-0.5 uppercase tracking-widest whitespace-nowrap">
                      {format.badge}
                    </span>
                  )}
                  {format.label}
                </button>
              ))}
            </div>

            {/* Description */}
            <p className="text-sm text-amber-200/55 mb-6 md:mb-8 leading-relaxed min-h-[40px]">
              {current.description}
            </p>

            {/* Bundle includes */}
            {selectedFormat === 'bundle' && (
              <div className="mb-6 md:mb-8 border border-amber-800/40 p-3 sm:p-4 bg-amber-950/30">
                <p className="text-xs uppercase tracking-widest text-amber-400 mb-2 sm:mb-3 font-bold">Includes:</p>
                <div className="flex items-start gap-2 mb-2">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-base mt-0.5 shrink-0"></i>
                  <span className="text-xs sm:text-sm text-amber-100/80">The Forgotten Felon Blueprint — Survival Guide</span>
                </div>
                <div className="flex items-start gap-2 mb-2">
                  <i className="ri-checkbox-circle-fill text-amber-500 text-base mt-0.5 shrink-0"></i>
                  <span className="text-xs sm:text-sm text-amber-100/80">The Forgotten Felon — Workbook</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-gift-fill text-amber-400 text-base mt-0.5 shrink-0"></i>
                  <span className="text-xs sm:text-sm text-amber-300 font-semibold">FREE Bonus Resource Guide included with purchase</span>
                </div>
              </div>
            )}

            {/* Bonus Resource — shown for non-bundle formats */}
            {selectedFormat !== 'bundle' && (
              <div className="mb-6 md:mb-8 border border-amber-500/30 p-3 sm:p-4 bg-amber-500/5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-gift-2-line text-amber-400 text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-400 font-black mb-1">Bonus Included</p>
                    <p className="text-xs sm:text-sm text-amber-100/75 leading-relaxed">
                      Every purchase comes with a <span className="text-amber-300 font-bold">FREE Bonus Resource Guide</span> — a curated list of tools, organizations, and next steps to help you hit the ground running.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Price */}
            <div className="mb-8 flex items-end gap-3">
              <div className="text-5xl sm:text-6xl font-black text-amber-50">
                {current.price}
              </div>
              {current.originalPrice && (
                <div className="mb-2">
                  <span className="text-base sm:text-lg text-amber-700/70 line-through">{current.originalPrice}</span>
                  <p className="text-xs text-amber-400 uppercase tracking-widest font-bold">You save $6.99</p>
                </div>
              )}
            </div>

            <a
              href={current.amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full max-w-xs h-12 sm:h-14 bg-amber-600 text-amber-50 text-xs sm:text-sm font-black uppercase tracking-widest hover:bg-amber-500 transition-all duration-300 shadow-lg hover:shadow-amber-700/40 mb-4 sm:mb-6 cursor-pointer"
            >
              {selectedFormat === 'bundle' ? 'Get the Bundle on Amazon' : 'Purchase Now on Amazon'}
              <i className="ri-arrow-right-line ml-2 text-base sm:text-lg"></i>
            </a>

            <div className="flex items-center space-x-3 text-amber-800/60 mt-3 sm:mt-4">
              <i className="ri-shield-check-line text-lg sm:text-xl text-amber-600/60"></i>
              <span className="text-xs uppercase tracking-widest">Secure Checkout via Amazon</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
